import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Loader2, Paperclip, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { facturasAPI } from '../api';
import { toast } from '../hooks/use-toast';

export const EnviarFacturaEmailDialog = ({ factura, trigger, onEnviado }) => {
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [info, setInfo] = useState(null);
  const [idioma, setIdioma] = useState('es');
  const [destinatario, setDestinatario] = useState('');
  const [asunto, setAsunto] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [copiaDespacho, setCopiaDespacho] = useState(false);

  const cargarInfo = useCallback(async (lang) => {
    setCargando(true);
    try {
      const res = await facturasAPI.getEmailInfo(factura.id, lang);
      setInfo(res.data);
      setDestinatario((prev) => prev || res.data?.destinatario_sugerido || '');
      setAsunto(res.data?.asunto_sugerido || '');
    } catch (e) {
      toast({ title: 'No se pudo preparar el envío', variant: 'destructive' });
    } finally {
      setCargando(false);
    }
  }, [factura.id]);

  useEffect(() => {
    if (abierto) cargarInfo(idioma);
  }, [abierto, idioma, cargarInfo]);

  const enviar = async () => {
    if (!destinatario.trim() || !destinatario.includes('@')) {
      toast({ title: 'Indica una dirección de email válida', variant: 'destructive' });
      return;
    }
    setEnviando(true);
    try {
      const res = await facturasAPI.enviarEmail(factura.id, {
        destinatario: destinatario.trim(),
        asunto: asunto.trim(),
        mensaje,
        idioma,
        copia_despacho: copiaDespacho,
      });
      toast({
        title: 'Factura enviada',
        description: `Enviada a ${res.data?.to}${res.data?.bcc?.length ? ' (con copia al despacho)' : ''}`,
      });
      setAbierto(false);
      setMensaje('');
      if (onEnviado) onEnviado();
    } catch (e) {
      toast({
        title: 'No se pudo enviar',
        description: e?.response?.data?.detail || 'Revisa la configuración de email',
        variant: 'destructive',
      });
    } finally {
      setEnviando(false);
    }
  };

  const enviadoAntes = info?.email_enviado_at;

  return (
    <Dialog open={abierto} onOpenChange={setAbierto}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" data-testid="enviar-factura-email-btn">
            <Mail className="w-4 h-4 mr-2" /> Enviar por email
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="enviar-factura-email-dialog">
        <DialogHeader>
          <DialogTitle>Enviar la factura {factura?.numero_factura} al cliente</DialogTitle>
        </DialogHeader>

        {cargando && !info ? (
          <p className="py-8 text-center text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Preparando...
          </p>
        ) : (
          <div className="space-y-4">
            {enviadoAntes && (
              <p
                className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3"
                data-testid="factura-email-ya-enviada"
              >
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Esta factura ya se envió el{' '}
                {new Date(enviadoAntes).toLocaleString('es-ES')} a <strong>{info.email_enviado_a}</strong>
                {info.email_enviado_por ? ` (por ${info.email_enviado_por})` : ''}.
              </p>
            )}
            {info && !info.smtp_configurado && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                No hay configuración de email en el sistema. Revísala en Configuración antes de enviar.
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs">Destinatario</Label>
                <Input
                  value={destinatario}
                  onChange={(e) => setDestinatario(e.target.value)}
                  placeholder="cliente@email.com"
                  data-testid="factura-email-destinatario"
                />
                {!info?.destinatario_sugerido && (
                  <p className="text-xs text-amber-600 mt-1">
                    El cliente no tiene email en su ficha: escríbelo aquí.
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs">Idioma</Label>
                <Select value={idioma} onValueChange={setIdioma}>
                  <SelectTrigger data-testid="factura-email-idioma">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="es">Castellano</SelectItem>
                    <SelectItem value="ca">Català</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Asunto</Label>
              <Input
                value={asunto}
                onChange={(e) => setAsunto(e.target.value)}
                data-testid="factura-email-asunto"
              />
            </div>

            <div>
              <Label className="text-xs">Mensaje personal (opcional)</Label>
              <Textarea
                rows={4}
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                placeholder="Se añadirá al principio del correo, antes del detalle de la factura."
                data-testid="factura-email-mensaje"
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="copia-despacho"
                checked={copiaDespacho}
                onCheckedChange={(v) => setCopiaDespacho(!!v)}
                data-testid="factura-email-copia"
              />
              <Label htmlFor="copia-despacho" className="text-sm font-normal cursor-pointer">
                Enviarme una copia oculta al despacho
              </Label>
            </div>

            <p className="text-xs text-gray-500 flex items-center gap-1">
              <Paperclip className="w-3 h-3" />
              Se adjuntará el PDF de la factura con tus datos de emisión.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
          <Button onClick={enviar} disabled={enviando || cargando} data-testid="factura-email-enviar-btn">
            {enviando
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EnviarFacturaEmailDialog;
